import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error | null;
  errorInfo?: { componentStack: string };
}

class ErrorBoundary extends React.Component<
  React.PropsWithChildren<{}>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<{}>) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: undefined };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Dashboard ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[400px] flex flex-col items-center justify-center p-8 text-center bg-white rounded-2xl border border-orange-100 shadow-lg">
          <div className="w-24 h-24 mb-6 rounded-2xl bg-orange-50 flex items-center justify-center">
            <svg className="w-12 h-12 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.667-1.54 0-3.268 0L4.732 13c-.77 1.667.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h2>
          <p className="text-slate-500 mb-8 max-w-md">
            We're sorry, but the dashboard encountered an unexpected error. This has been logged and will be fixed soon.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 rounded-xl bg-orange-500 text-white font-semibold hover:bg-orange-600 transition"
            >
              Reload Page
            </button>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition"
            >
              Go Back
            </button>
          </div>
          {this.state.error && (
            <details className="mt-8 p-4 bg-orange-50 rounded-xl max-w-md text-xs text-slate-600">
              <summary className="font-medium cursor-pointer mb-2">Technical details (click to expand)</summary>
              <pre className="whitespace-pre-wrap font-mono text-slate-700 mt-2">
                {this.state.error.message}
                {this.state.errorInfo?.componentStack && (
                  <>
                    <br />
                    <br />
                    {this.state.errorInfo.componentStack}
                  </>
                )}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

